import {
  type ClientDispatchedToolArgs,
  type ClientDispatchedToolResult,
  type PrescriptionProduct,
  ZONING_TIMEOUT_MS,
  ZoneEdges,
  ZoningMethod,
  ZoningStatus
} from 'api-validations/aurora'
import { useEffect } from 'react'
import { useAuroraIncomingHandlers } from 'shared-ui/aurora'
import { BreakMethod } from 'utils'
import { create } from 'zustand'

import {
  EdgeRounding,
  SMALLEST_ZONES,
  type SmallestZone,
  ZONE_SIZES,
  type ZoneSelection,
  ZoneSelectionKind,
  type ZoneSource
} from '../api/useZoneTuning'

export type ZoneTuningSettings = {
  method: BreakMethod
  selection: ZoneSelection
  smallestZone: SmallestZone
  edges: EdgeRounding
}

type Zoning = ClientDispatchedToolResult<'DescribeZoneTuning'>
type ZoningReady = Extract<Zoning, { status: ZoningStatus.Ready }>

type ZoningSnapshot = { key: string; zoning: Zoning | undefined }

const REPLY_HEADROOM_MS = 10_000

const METHOD_OF: Record<ZoningMethod, BreakMethod> = {
  [ZoningMethod.NaturalBreaks]: BreakMethod.NaturalBreaks,
  [ZoningMethod.EqualCount]: BreakMethod.EqualCount,
  [ZoningMethod.EqualInterval]: BreakMethod.EqualInterval
}

const ZONING_METHOD_OF: Record<BreakMethod, ZoningMethod> = {
  [BreakMethod.NaturalBreaks]: ZoningMethod.NaturalBreaks,
  [BreakMethod.EqualCount]: ZoningMethod.EqualCount,
  [BreakMethod.EqualInterval]: ZoningMethod.EqualInterval
}

const EDGES_OF: Record<ZoneEdges, EdgeRounding> = {
  [ZoneEdges.Straight]: EdgeRounding.Straight,
  [ZoneEdges.Rounded]: EdgeRounding.Rounded,
  [ZoneEdges.VeryRounded]: EdgeRounding.VeryRounded
}

const ZONE_EDGES_OF: Record<EdgeRounding, ZoneEdges> = {
  [EdgeRounding.Straight]: ZoneEdges.Straight,
  [EdgeRounding.Rounded]: ZoneEdges.Rounded,
  [EdgeRounding.VeryRounded]: ZoneEdges.VeryRounded
}

export const withoutCustomBreaks = (selection: ZoneSelection): ZoneSelection =>
  selection.kind === ZoneSelectionKind.Custom
    ? { kind: ZoneSelectionKind.Count, zoneCount: selection.breaks.length + 1 }
    : selection

const nearestSmallestZone = (acres: number): SmallestZone =>
  SMALLEST_ZONES.reduce((nearest, candidate) =>
    Math.abs(ZONE_SIZES[candidate].minZoneSize - acres) <
    Math.abs(ZONE_SIZES[nearest].minZoneSize - acres)
      ? candidate
      : nearest
  )

const applyToolArgs = (
  settings: ZoneTuningSettings,
  { zoneCount, method, smallestZoneAcres, edges }: ClientDispatchedToolArgs<'SetZoneTuning'>
): ZoneTuningSettings => {
  const selection: ZoneSelection =
    zoneCount === null
      ? withoutCustomBreaks(settings.selection)
      : { kind: ZoneSelectionKind.Count, zoneCount }
  return {
    method: method === null ? settings.method : METHOD_OF[method],
    selection: method === null && zoneCount === null ? settings.selection : selection,
    smallestZone:
      smallestZoneAcres === null ? settings.smallestZone : nearestSmallestZone(smallestZoneAcres),
    edges: edges === null ? settings.edges : EDGES_OF[edges]
  }
}

export const zoningKey = ({
  source,
  settings
}: {
  source: ZoneSource
  settings: ZoneTuningSettings
}) => JSON.stringify({ source, settings })

export const readyZoning = ({
  settings,
  smoothsEdges,
  ...zoning
}: Omit<ZoningReady, 'status' | 'customBreaks' | 'method' | 'smallestZoneAcres' | 'edges'> & {
  settings: ZoneTuningSettings
  smoothsEdges: boolean
}): ZoningReady => ({
  status: ZoningStatus.Ready,
  ...zoning,
  customBreaks: settings.selection.kind === ZoneSelectionKind.Custom,
  method: ZONING_METHOD_OF[settings.method],
  smallestZoneAcres: smoothsEdges ? ZONE_SIZES[settings.smallestZone].minZoneSize : null,
  edges: smoothsEdges ? ZONE_EDGES_OF[settings.edges] : null
})

const useZoningStore = create<{ snapshot: ZoningSnapshot | null }>(() => ({ snapshot: null }))

const SCREEN_CLOSED: Zoning = {
  status: ZoningStatus.Failed,
  reason: 'The user cleared the zone layer or left the zoning screen before the zones were made.'
}

const settledZoning = (key: string) =>
  new Promise<Zoning>((resolve) => {
    const settledFor = (snapshot: ZoningSnapshot | null) => {
      if (snapshot === null) return SCREEN_CLOSED
      return snapshot.key === key ? snapshot.zoning : undefined
    }

    const current = settledFor(useZoningStore.getState().snapshot)
    if (current !== undefined) {
      resolve(current)
      return
    }

    const expiry = setTimeout(() => {
      unsubscribe()
      resolve({ status: ZoningStatus.Failed, reason: 'The zones took too long to make.' })
    }, ZONING_TIMEOUT_MS - REPLY_HEADROOM_MS)
    const unsubscribe = useZoningStore.subscribe(({ snapshot }) => {
      const settled = settledFor(snapshot)
      if (settled === undefined) return
      clearTimeout(expiry)
      unsubscribe()
      resolve(settled)
    })
  })

export const useZoneTuningAuroraTools = ({
  source,
  settings,
  zoning,
  onSettingsChange,
  acceptZones
}: {
  source: ZoneSource
  settings: ZoneTuningSettings
  zoning: Zoning | undefined
  onSettingsChange: (settings: ZoneTuningSettings) => void
  acceptZones: (products: PrescriptionProduct[]) => void
}) => {
  const key = zoningKey({ source, settings })

  useEffect(() => {
    useZoningStore.setState({ snapshot: { key, zoning } })
  }, [key, zoning])
  useEffect(() => () => useZoningStore.setState({ snapshot: null }), [])

  useAuroraIncomingHandlers({
    dispatch: {
      DescribeZoneTuning: () => settledZoning(key),
      SetZoneTuning: (args) => {
        const next = applyToolArgs(settings, args)
        onSettingsChange(next)
        return settledZoning(zoningKey({ source, settings: next }))
      },
      AcceptZones: async ({ products }) => {
        if (zoning?.status !== ZoningStatus.Ready) {
          return {
            accepted: false,
            reason: 'The zones are still being made, or could not be made from this layer.'
          }
        }
        acceptZones(products)
        return {
          accepted: true,
          zones: zoning.zones.map(({ name, acres }) => ({ name, acres }))
        }
      }
    }
  })
}
